import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileText, Filter } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-muted text-muted-foreground',
  in_review: 'bg-teal/15 text-teal-light border-teal/30',
  approved: 'bg-compliance-low/15 text-compliance-low border-compliance-low/30',
  written_back: 'bg-purple-100 text-purple-800 border-purple-200',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  in_review: 'In Review',
  approved: 'Approved',
  written_back: 'Written Back',
};

export function PolicyReviewListPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  const { data, isLoading } = useQuery({
    queryKey: ['policy-review-sessions', statusFilter],
    queryFn: () => api.policyReview.sessions(statusFilter ? { status: statusFilter } : undefined),
    enabled: !!user,
  });

  const sessions = data?.sessions ?? [];

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
      <PageHeader
        title="Policy Reviews"
        subtitle="AI-powered policy impact analysis and section-level review"
      />

      {/* Filters */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Status:</span>
        {[undefined, 'in_review', 'approved', 'written_back'].map((s) => (
          <Button
            key={s ?? 'all'}
            variant={statusFilter === s ? 'default' : 'outline'}
            size="sm"
            className={cn('h-7 text-xs', statusFilter === s && 'bg-teal text-white')}
            onClick={() => setStatusFilter(s)}
          >
            {s ? STATUS_LABELS[s] : 'All'}
          </Button>
        ))}
      </div>

      {/* Sessions table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal border-t-transparent" />
        </div>
      ) : sessions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12">
            <FileText className="h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No policy review sessions found.</p>
            <p className="text-xs text-muted-foreground">
              Start by analyzing policies from a compliance project.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Document</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Progress</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Created</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground" />
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => {
                const reviewed = session.acceptedCount + session.rejectedCount;
                const progressPct = session.sectionCount > 0 ? (reviewed / session.sectionCount) * 100 : 0;

                return (
                  <tr
                    key={session.sessionId}
                    className="cursor-pointer border-b border-border last:border-b-0 hover:bg-muted/30"
                    onClick={() => navigate(`/policy-review/${session.sessionId}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div>
                          <p className="font-medium text-foreground">{session.documentName}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-xs">{session.documentPath}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={cn('text-[10px]', STATUS_STYLES[session.status])}>
                        {STATUS_LABELS[session.status] ?? session.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Progress value={progressPct} className="h-1.5 w-20" />
                        <span className="text-xs text-muted-foreground">
                          {reviewed}/{session.sectionCount}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {new Date(session.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-teal">
                        Review
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
